import { z } from 'zod';

import type { MicePremisesKey } from './mice';

/** As 9 regras de viagem do evento, na ordem da ficha. "N de 9" = 9 − `rulesPending.length`.
 *  Não é o mesmo que as chaves das premissas: três delas moram em `destinations[]`. */
export const MICE_EVENT_RULES = [
  'destinations',
  'arrival',
  'arrivalMargin',
  'redEye',
  'return',
  'checkedBaggage',
  'savingsReviewThreshold',
  'rooming',
  'transfer',
] as const;
export const MiceEventRuleSchema = z.enum(MICE_EVENT_RULES);
export type MiceEventRule = z.infer<typeof MiceEventRuleSchema>;

/** Chave das premissas em que cada regra grava. */
export const RULE_KEY: Record<MiceEventRule, MicePremisesKey> = {
  destinations: 'destinations',
  arrival: 'destinations',
  arrivalMargin: 'destinations',
  redEye: 'redEye',
  return: 'returnDeparture',
  checkedBaggage: 'checkedBaggage',
  savingsReviewThreshold: 'savingsReviewThreshold',
  rooming: 'rooming',
  transfer: 'transfer',
};

/** Chaves extras que a pílula da regra também lê e fixa, além da `RULE_KEY`. A pílula da margem
 *  tem a 4ª escolha "dia anterior" (`arrivalDayBefore`); escolher 0/30/60 não grava esse campo. */
const EXTRA_RULE_KEYS: Partial<Record<MiceEventRule, MicePremisesKey[]>> = {
  arrivalMargin: ['arrivalDayBefore'],
};

/** Todas as chaves das premissas da regra, para pendência e pílula (a edição com qualquer uma
 *  delas tira a regra das pendentes). Não serve ao merge do extrator, que segue a `RULE_KEY`. */
export function ruleKeys(rule: MiceEventRule): MicePremisesKey[] {
  return [RULE_KEY[rule], ...(EXTRA_RULE_KEYS[rule] ?? [])];
}

/** Regras em aberto: as pendentes e, num evento antigo com o rascunho incompleto, as regras das
 *  chaves que faltam (`missingPremises`), na ordem da ficha. */
export function openEventRules(view: {
  rulesPending: readonly MiceEventRule[];
  missingPremises: readonly MicePremisesKey[];
}): MiceEventRule[] {
  const missing = new Set<MicePremisesKey>(view.missingPremises);
  return MICE_EVENT_RULES.filter(
    (rule) => view.rulesPending.includes(rule) || missing.has(RULE_KEY[rule]),
  );
}

/** Valor de uma escolha pronta, discriminado pela regra. */
export type EventRuleValue =
  | { rule: 'destinations' }
  | { rule: 'arrival'; time: '10:00' | '12:00' | '14:00' }
  | { rule: 'arrivalMargin'; minutes: 0 | 30 | 60 }
  /** Liga só `arrivalDayBefore`; a margem de minutos fica como estava. */
  | { rule: 'arrivalMargin'; dayBefore: true }
  /** `avoid` = madrugada 00:00-06:00; `allow` = sem janela (início === fim, 00:00). */
  | { rule: 'redEye'; avoid: boolean }
  /** `from` nulo = qualquer horário (`earliestIdeal` 00:00). */
  | { rule: 'return'; from: string | null }
  | { rule: 'checkedBaggage'; checked: boolean }
  /** `amount` nulo = nunca revisar. */
  | { rule: 'savingsReviewThreshold'; amount: number | null }
  /** `occupancy` nulo = sem hospedagem (`rooming: null`). */
  | {
      rule: 'rooming';
      occupancy: 1 | 2 | null;
      genderRule: 'same_gender' | 'any';
    }
  /** `groupMinutes` nulo = sem transfer (`transfer: null`). */
  | { rule: 'transfer'; groupMinutes: 30 | 60 | 90 | 120 | null };

export type EventRuleChoice = { id: string; value: EventRuleValue };

const arrival = (time: '10:00' | '12:00' | '14:00'): EventRuleChoice => ({
  id: time,
  value: { rule: 'arrival', time },
});
const margin = (minutes: 0 | 30 | 60): EventRuleChoice => ({
  id: String(minutes),
  value: { rule: 'arrivalMargin', minutes },
});
const returnFrom = (id: string, from: string | null): EventRuleChoice => ({
  id,
  value: { rule: 'return', from },
});
const saving = (id: string, amount: number | null): EventRuleChoice => ({
  id,
  value: { rule: 'savingsReviewThreshold', amount },
});
const room = (
  id: string,
  occupancy: 1 | 2 | null,
  genderRule: 'same_gender' | 'any',
): EventRuleChoice => ({ id, value: { rule: 'rooming', occupancy, genderRule } });

const transfer = (id: string, groupMinutes: 30 | 60 | 90 | 120 | null): EventRuleChoice => ({
  id,
  value: { rule: 'transfer', groupMinutes },
});

/** Escolhas prontas de cada regra (a ficha mostra os botões; o agente fala os mesmos ids). */
export const EVENT_RULE_CHOICES: Record<MiceEventRule, readonly EventRuleChoice[]> = {
  destinations: [],
  arrival: [arrival('10:00'), arrival('12:00'), arrival('14:00')],
  arrivalMargin: [
    margin(0),
    margin(30),
    margin(60),
    { id: 'day_before', value: { rule: 'arrivalMargin', dayBefore: true } },
  ],
  redEye: [
    { id: 'avoid', value: { rule: 'redEye', avoid: true } },
    { id: 'allow', value: { rule: 'redEye', avoid: false } },
  ],
  return: [
    returnFrom('15:00', '15:00'),
    returnFrom('17:00', '17:00'),
    returnFrom('19:00', '19:00'),
    returnFrom('any', null),
  ],
  checkedBaggage: [
    { id: 'carry_on', value: { rule: 'checkedBaggage', checked: false } },
    { id: 'checked', value: { rule: 'checkedBaggage', checked: true } },
  ],
  savingsReviewThreshold: [saving('200', 200), saving('500', 500), saving('never', null)],
  rooming: [
    room('single', 1, 'any'),
    room('double_same', 2, 'same_gender'),
    room('double_any', 2, 'any'),
    room('none', null, 'any'),
  ],
  transfer: [
    transfer('none', null),
    transfer('30', 30),
    transfer('60', 60),
    transfer('90', 90),
    transfer('120', 120),
  ],
};

/** Aviso que a ficha manda ao agente (edição, publicação, reabertura). Viaja como `selections`
 *  de uma confirmation de contexto: não resolve nenhum challenge. */
export const EventSheetNoticeSchema = z
  .object({
    kind: z.literal('event_sheet_notice'),
    eventId: z.string().min(1),
    notice: z.enum(['edited', 'published', 'reopen']),
    rules: z.array(MiceEventRuleSchema).optional(),
  })
  .strict();
export type EventSheetNotice = z.infer<typeof EventSheetNoticeSchema>;

export const eventSheetChallengeId = (eventId: string): string => `event-sheet:${eventId}`;
